import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, EventEmitter, HostListener, Input, Output, Signal, signal, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-form-list',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './form-list.component.html',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0, transform: 'translateY(0)' }), animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))]),
      transition(':leave', [animate('100ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
})
export class FormListComponent implements OnInit {
  private _control: FormControl = new FormControl();
  private selectedValues: number[] = [];

  @Input() set control(value: AbstractControl<any, any> | null) {
    this._control = (value as FormControl) ?? new FormControl();
  }

  @Input() id!: string;
  @Input() label!: string;
  @Input() options!: Signal<Map<number, string>>;
  @Input() placeholder = 'Seleccione una opción';
  @Input() multiple = false;
  @Input() allowSearch = false;
  @Input() required = false;
  @Input() defaultValue: number | number[] | null = null;

  @Output() selectionChange = new EventEmitter<number | number[] | null>();

  filteredOptions = computed(() => {
    // Si la búsqueda está deshabilitada, mostramos todas las opciones
    if (!this.allowSearch) {
      return this.transformOptions(this.options());
    }

    // Si la búsqueda está habilitada, aplicamos el filtro
    const term = this.searchTerm().toLowerCase();
    return this.transformOptions(this.options()).filter((option) => option.value.toLowerCase().includes(term));
  });
  isOpen = signal(false);
  searchTerm = signal('');

  get control(): FormControl {
    return this._control;
  }

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    this.setInitialValue();

    // TODO: optimizar
    this.control.valueChanges.subscribe((value) => {
      if (!value) {
        this.searchTerm.set('');
      } else {
        this.searchTerm.set(this.options().get(value) ?? '');
      }
    });
  }

  private setInitialValue() {
    const value = this.control.value ?? this.defaultValue;

    if (value !== undefined) {
      let displayValue = '';

      if (Array.isArray(value)) {
        this.selectedValues = [...value];
        displayValue = this.selectedValues
          .map((key) => this.options().get(key) ?? '')
          .filter((label) => label !== '')
          .join(', ');
      } else {
        this.selectedValues = value !== null ? [value] : [];
        displayValue = this.options().get(value) ?? '';
      }

      this.searchTerm.set(displayValue);

      if (!this.control) {
        this.selectionChange.emit(value);
      }
    }
  }

  transformOptions(options: Map<number, string>): { key: number; value: string }[] {
    const transformed = Array.from(options, ([key, value]) => ({ key, value }));
    if (!this.required) {
      transformed.unshift({ key: -1, value: '---' });
    }

    return transformed;
  }

  toggleDropdown() {
    this.isOpen.set(!this.isOpen());
  }

  updateSearchTerm(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }

  selectOption(key: number, value: string) {
    if (key === -1) {
      this.clearSelection();
      return;
    }

    this.multiple ? this.handleMultipleSelection(key) : this.handleSingleSelection(key);
  }

  private clearSelection() {
    this.setValue(null);
    this.searchTerm.set('');
  }

  private handleMultipleSelection(key: number) {
    const isSelected = this.selectedValues.includes(key);

    this.selectedValues = isSelected
      ? this.selectedValues.filter((item) => item !== key) // Eliminar si ya estaba seleccionado
      : [...this.selectedValues, key]; // Agregar si no estaba seleccionado

    this.setValue([...this.selectedValues]); // 🔥 Forzar nueva referencia
  }

  private handleSingleSelection(key: number) {
    this.setValue(key);
    this.isOpen.set(false);
  }

  private setValue(value: number | number[] | null) {
    this._control.setValue(value);
    this.selectionChange.emit(value);

    this.selectedValues = Array.isArray(value) ? [...value] : value !== null ? [value] : [];
    const displayValue = Array.isArray(value) ? this.selectedValues.map((v) => this.options().get(v)).join(', ') : (this.options().get(value!) ?? '');

    this.searchTerm.set(displayValue);
  }

  isSelected(key: number): boolean {
    return this.selectedValues.includes(key);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }
}
